import { useEffect, useState } from "react";
import {
  Radar, Play, Upload, Mail, Download, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, Clock, FileText, Building2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";


// BLOCK 152 ,  persist the in-flight upload job id so a page refresh can
// resume showing progress of a background job that's still running server-side.
const TAX_JOB_STORAGE_KEY = "acquigine_tax_upload_job";

const SCHEDULE_LABEL = {
  daily: "Daily",
  weekly: "Weekly",
  quarterly: "Quarterly",
  "on-write": "Real-time",
  "operator-upload": "Operator upload",
};

// BLOCK 150 ,  real PUBLIC_-prefixed report names catalogued from
// county-taxes.net (GovHub/Grant Street). The platform blocks automated
// report generation (Cloudflare), so these are manual-download-then-upload
// targets, not connectors. See /app/memory/block148_149_report.md and
// block150_report.md for the full catalog + evidence.
const PUBLIC_REPORT_GUIDE = {
  Broward: {
    portal: "https://county-taxes.net/broward/reports",
    reports: [
      "PUBLIC_BROWARD UNPAID REAL ESTATE (Real estate category ,  broadest current unpaid roll)",
      "PUBLIC_ALL Certificates (Paid & Unpaid) (Real estate ,  full certificate status)",
      "PUBLIC_BROWARD_UNPAID BY USE CODE (Real estate ,  pre-filtered by property use)",
      "SEARCH OUTSTANDING WARRANTS_PUBLIC (Tangible property)",
    ],
    cadence: "Monthly ,  Broward's own delinquency/certificate status changes on a monthly billing cycle, not daily.",
  },
  Martin: {
    portal: "https://county-taxes.net/fl-martin/reports",
    reports: [
      "Public - All Delinquent Taxes (Real estate)",
      "Public - All Certificates (Real estate)",
      "Public - County-Held Certificates (Real estate)",
      "Public - Account Search (Tangible property)",
    ],
    cadence: "Monthly ,  Martin's pool is small (~8,200 parcels); a monthly pull is more than enough.",
  },
  "Miami-Dade": {
    portal: "https://county-taxes.net/fl-miamidade/reports",
    reports: [
      "Public-R/E Delinq List (Real estate ,  current delinquent list)",
      "Public-litig/bkrpt accts (Real estate ,  litigation/bankruptcy accounts, high-value signal)",
      "Public-Open Certificates wAddr (Real estate ,  open certs with address)",
      "Public-Tangible Roll (last 3 years) (Tangible property)",
    ],
    cadence: "Monthly ,  Miami-Dade's roll (~100k parcels) is the largest of the 3; a monthly cadence balances freshness against a large manual download/upload.",
  },
};


function StatusPill({ run, sourceStatus }) {
  // Source-level disabled state (e.g., paid API not funded) takes precedence.
  if (sourceStatus === "disabled") {
    return (
      <span
        data-testid="status-disabled"
        className="text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 font-mono bg-slate-500/20 text-slate-400 border-slate-500/40"
      >
        Disabled
      </span>
    );
  }
  if (sourceStatus === "scaffolded") {
    return (
      <span
        data-testid="status-scaffolded"
        title="Architecture ready; upstream endpoints not yet wired. Running this connector today ingests zero rows."
        className="text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 font-mono bg-amber-500/10 text-amber-300 border-amber-500/40"
      >
        Not yet ingesting
      </span>
    );
  }
  if (!run) {
    return <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Never run</span>;
  }
  const status = run.status || "Unknown";
  const cls = {
    Success: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    Partial: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    NotAvailable: "bg-slate-500/15 text-slate-300 border-slate-500/40",
    Disabled: "bg-slate-500/20 text-slate-400 border-slate-500/40",
    Failed: "bg-red-500/15 text-red-300 border-red-500/40",
  }[status] || "bg-slate-500/15 text-slate-300 border-slate-500/40";
  return (
    <span className={`text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 font-mono ${cls}`}>
      {status}
    </span>
  );
}


function fmtDate(iso) {
  if (!iso) return "Never";
  try { return fmtDateTime(iso); } catch { return iso; }
}


export default function DiscoveryAdmin() {
  const [sources, setSources] = useState([]);
  const [running, setRunning] = useState({});
  const [recalcing, setRecalcing] = useState(false);
  const [busyFsa, setBusyFsa] = useState(false);
  const [counties, setCounties] = useState([]);
  const [county, setCounty] = useState({ county: "", state: "" });
  const [taxCsvBusy, setTaxCsvBusy] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [lastTaxUpload, setLastTaxUpload] = useState(null);
  const [sunshineRequests, setSunshineRequests] = useState([]);
  const [sunshineBusy, setSunshineBusy] = useState({});
  const [replyDraft, setReplyDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState({});

  const load = async () => {
    setLoading(true);
    const errs = {};
    try {
      const r = await api.get("/discovery/sources");
      setSources(r.data.sources || []);
    } catch (e) {
      errs.sources = "Failed to load sources";
      toast.error(errs.sources);
    }
    try {
      const c = await api.get("/discovery/tax-delinquent/counties");
      setCounties(c.data.counties || []);
    } catch (e) {
      errs.counties = "Failed to load counties ,  retry below";
    }
    try {
      const sl = await api.get("/admin/sunshine-law/requests");
      setSunshineRequests(sl.data.requests || []);
    } catch (e) {
      errs.sunshine = "Failed to load Sunshine Law requests ,  retry below";
    }
    setLoadErrors(errs);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const saved = localStorage.getItem(TAX_JOB_STORAGE_KEY);
    if (saved) {
      try {
        const { jobId, filename } = JSON.parse(saved);
        if (jobId) {
          setTaxCsvBusy(true);
          pollTaxJob(jobId, filename);
        }
      } catch { /* ignore malformed storage */ }
    }
  }, []);

  const runSource = async (id) => {
    setRunning((m) => ({ ...m, [id]: true }));
    try {
      const r = await api.post(`/discovery/sources/${id}/run`);
      const out = r.data;
      if (out.status === "disabled_no_key" || out.status === "disabled") {
        toast.info(out.message || `${id}: disabled`);
      } else if (out.status === "not_available") {
        toast.warning(`${id}: source not available (API key needed)`);
      } else if (out.status === "failed") {
        toast.error(`${id}: ${out.error || "failed"}`);
      } else {
        toast.success(`${id}: ${out.found || 0} found · ${out.created || 0} created · ${out.updated || 0} updated`);
      }
      load();
    } catch (e) {
      toast.error(`Run failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning((m) => ({ ...m, [id]: false }));
    }
  };

  const runAll = async () => {
    if (!window.confirm("Run ALL Tier 1 sources now? This may take a few minutes.")) return;
    setRunning((m) => ({ ...m, all: true }));
    try {
      const r = await api.post("/discovery/run-all");
      toast.success("Full discovery run complete.");
      console.log("discovery results:", r.data);
      load();
    } catch (e) {
      toast.error("Run-all failed");
    } finally {
      setRunning((m) => ({ ...m, all: false }));
    }
  };

  const recalc = async () => {
    setRecalcing(true);
    try {
      const r = await api.post("/discovery/recalculate-scores");
      toast.success(`Rescored ${r.data.scored} / ${r.data.checked} opportunities`);
    } catch (e) {
      toast.error("Recalc failed");
    } finally {
      setRecalcing(false);
    }
  };

  const uploadFsa = async (file) => {
    if (!file) return;
    setBusyFsa(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/discovery/usda-fsa/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`FSA upload: ${r.data.created || 0} created, ${r.data.updated || 0} updated`);
      load();
    } catch (e) {
      toast.error(`FSA upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyFsa(false);
    }
  };

  const uploadTax = async (file) => {
    if (!file || !county.county || !county.state) {
      toast.error("Select a county and state first");
      return;
    }
    setTaxCsvBusy(true);
    setLastTaxUpload({
      ok: null, processing: true, filename: file.name, county: county.county, state: county.state,
      processed_rows: 0, total_rows: 0, created: 0, updated: 0, skipped: 0, at: new Date().toISOString(),
    });
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("county", county.county);
      fd.append("state", county.state);
      const r = await api.post("/discovery/tax-delinquent/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = r.data.job_id;
      if (!jobId) throw new Error("No job_id returned");
      toast.info("Upload accepted ,  processing in the background, this can take a while for large files");
      localStorage.setItem(TAX_JOB_STORAGE_KEY, JSON.stringify({ jobId, filename: file.name }));
      pollTaxJob(jobId, file.name);
    } catch (e) {
      setLastTaxUpload({
        ok: false, filename: file.name, county: county.county, state: county.state,
        error: e?.response?.data?.detail || e.message, at: new Date().toISOString(),
      });
      toast.error(`Tax file upload failed: ${e?.response?.data?.detail || e.message}`);
      setTaxCsvBusy(false);
    }
  };

  const pollTaxJob = (jobId, filename) => {
    let failCount = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/discovery/tax-delinquent/upload-status/${jobId}`);
        failCount = 0;
        const job = r.data;
        const done = job.status === "completed" || job.status === "failed";
        setLastTaxUpload({
          ok: job.status === "completed" ? true : (job.status === "failed" ? false : null),
          processing: !done,
          parsing: job.status === "parsing" || job.status === "queued",
          filename: job.filename || filename,
          county: job.county, state: job.state,
          found: job.total_rows || 0,
          processed_rows: job.processed_rows || 0,
          total_rows: job.total_rows || 0,
          created: job.created || 0, updated: job.updated || 0,
          skipped: job.skipped || 0, errors: job.errors || 0,
          error: job.error,
          unmapped_columns: job.unmapped_columns || [],
          at: job.updated_at || new Date().toISOString(),
        });
        if (done) {
          setTaxCsvBusy(false);
          localStorage.removeItem(TAX_JOB_STORAGE_KEY);
          if (job.status === "failed") {
            toast.error(`Tax upload failed: ${job.error || "could not process file"}`);
          } else {
            toast.success(`Tax file processed: ${job.created || 0} created, ${job.updated || 0} updated`);
          }
          load();
          return;
        }
        setTimeout(poll, 2500);
      } catch (e) {
        // Transient network blip shouldn't abandon a 15-minute background
        // job's progress tracking ,  retry a few times before giving up.
        failCount += 1;
        if (failCount <= 3) {
          setTimeout(poll, 2500);
          return;
        }
        setTaxCsvBusy(false);
        toast.error("Lost track of upload progress ,  check the sources table shortly");
      }
    };
    poll();
  };

  const genEmail = async () => {
    if (!county.county || !county.state) {
      toast.error("Select a county first");
      return;
    }
    try {
      const r = await api.post("/discovery/tax-delinquent/email-template", {
        county: county.county, state: county.state,
      });
      setEmailTemplate(r.data.template);
    } catch (e) {
      toast.error("Failed to generate template");
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(emailTemplate);
    toast.success("Email template copied");
  };

  const markReplied = async (countyName) => {
    const draft = replyDraft[countyName] || {};
    if (!draft.note || !draft.note.trim()) {
      toast.error("Enter what the county said before marking as replied");
      return;
    }
    setSunshineBusy((m) => ({ ...m, [countyName]: true }));
    try {
      await api.post(`/admin/sunshine-law/requests/${encodeURIComponent(countyName)}/mark-replied`, {
        reply_note: draft.note,
        has_attachment: !!draft.hasAttachment,
        attachment_description: draft.attachmentDesc || "",
      });
      toast.success(`${countyName}: marked as replied`);
      setReplyDraft((m) => ({ ...m, [countyName]: {} }));
      load();
    } catch (e) {
      toast.error(`Failed to save: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSunshineBusy((m) => ({ ...m, [countyName]: false }));
    }
  };

  const resetReply = async (countyName) => {
    setSunshineBusy((m) => ({ ...m, [countyName]: true }));
    try {
      await api.post(`/admin/sunshine-law/requests/${encodeURIComponent(countyName)}/reset`);
      toast.success(`${countyName}: reset to pending`);
      load();
    } catch (e) {
      toast.error(`Failed to reset: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSunshineBusy((m) => ({ ...m, [countyName]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="discovery-admin-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Lead Discovery Engine</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Auto-discover acquisition opportunities from public-records sources. Every new lead
            enters the scoring queue. Score &gt;= 55 surfaces in the main pipeline; lower scores route
            to the Discovery Queue.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={recalc} disabled={recalcing}
                  data-testid="discovery-recalc-all"
                  className="btn-ghost text-xs inline-flex items-center gap-2">
            <RefreshCw className={`h-3.5 w-3.5 ${recalcing ? "animate-spin" : ""}`} />
            {recalcing ? "Rescoring..." : "Recalculate all scores"}
          </button>
          <button onClick={runAll} disabled={running.all}
                  data-testid="discovery-run-all"
                  className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-2">
            <Play className="h-3.5 w-3.5" /> {running.all ? "Running..." : "Run all Tier 1"}
          </button>
        </div>
      </header>

      {/* Sources table */}
      <div className="ae-card overflow-hidden">
        <div className="px-5 py-3 border-b border-white/8 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
          <Radar className="h-3 w-3" /> Tier 1 sources
        </div>
        <table className="w-full text-sm" data-testid="discovery-sources-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 bg-white/3">
              <th className="text-left px-5 py-3 font-medium">Source</th>
              <th className="text-left px-3 py-3 font-medium">Schedule</th>
              <th className="text-left px-3 py-3 font-medium">Key</th>
              <th className="text-left px-3 py-3 font-medium">Last run</th>
              <th className="text-left px-3 py-3 font-medium">Status</th>
              <th className="text-right px-5 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id} className="border-t border-white/5 hover:bg-white/3" data-testid={`discovery-source-${s.id}`}>
                <td className="px-5 py-3">
                  <div className="font-medium text-slate-200">{s.label}</div>
                  <div className="text-[10px] text-slate-500">Tier {s.tier}{s.category ? ` · ${s.category}` : ""}</div>
                  {s.status === "disabled" && s.disabled_message && (
                    <div
                      data-testid={`discovery-source-${s.id}-disabled-note`}
                      className="mt-1 text-[10px] text-slate-400 max-w-md leading-snug"
                    >
                      {s.disabled_message}
                    </div>
                  )}
                  {s.status !== "disabled" && s.description && (
                    <div className="mt-1 text-[10px] text-slate-500 max-w-md leading-snug">
                      {s.description}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-xs text-slate-400">{SCHEDULE_LABEL[s.schedule] || s.schedule}</td>
                <td className="px-3 py-3">
                  {s.key_env ? (
                    s.key_configured ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300 font-mono">
                        <CheckCircle2 className="h-3 w-3" /> Configured
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-300 font-mono" title={`Set ${s.key_env} in backend/.env`}>
                        <AlertTriangle className="h-3 w-3" /> Needs key
                      </span>
                    )
                  ) : (
                    <span className="text-[10px] text-slate-500 font-mono">No key required</span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs text-slate-400 font-mono">
                  {fmtDate(s.last_run?.timestamp)}
                </td>
                <td className="px-3 py-3"><StatusPill run={s.last_run} sourceStatus={s.status} /></td>
                <td className="px-5 py-3 text-right">
                  {s.schedule === "operator-upload" ? (
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">Use CSV uploader below</span>
                  ) : s.status === "disabled" ? (
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider" data-testid={`discovery-run-${s.id}-blocked`}>
                      Disabled
                    </span>
                  ) : s.status === "scaffolded" ? (
                    <span
                      className="text-[10px] text-amber-300/80 uppercase tracking-wider"
                      data-testid={`discovery-run-${s.id}-scaffolded`}
                      title="Not yet ingesting ,  endpoints pending Priority 3 work."
                    >
                      Pending build
                    </span>
                  ) : (
                    <button
                      onClick={() => runSource(s.id)}
                      disabled={running[s.id]}
                      data-testid={`discovery-run-${s.id}`}
                      className="btn-ghost text-xs inline-flex items-center gap-1.5"
                    >
                      <Play className={`h-3 w-3 ${running[s.id] ? "animate-pulse" : ""}`} />
                      {running[s.id] ? "Running..." : "Run"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CSV uploaders */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* USDA FSA upload */}
        <div className="ae-card p-5 space-y-3" data-testid="discovery-fsa-uploader">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-emerald-400" />
            <h3 className="font-heading text-lg">USDA FSA Delinquency</h3>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Upload a quarterly FSA farm-loan delinquency CSV. Records with delinquent or foreclosure
            status auto-create Land Opportunities.
          </p>
          <label className="block">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => uploadFsa(e.target.files?.[0])}
              disabled={busyFsa}
              data-testid="discovery-fsa-file"
              className="block w-full text-xs text-slate-400 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border file:border-emerald-500/40 file:bg-emerald-500/15 file:text-emerald-300 file:text-[10px] file:uppercase file:tracking-wider hover:file:bg-emerald-500/25"
            />
          </label>
        </div>

        {/* Tax Delinquent upload + email template */}
        <div className="ae-card p-5 space-y-3" data-testid="discovery-tax-uploader">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-emerald-400" />
            <h3 className="font-heading text-lg">County Tax Delinquent</h3>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Request public delinquency CSVs from county treasurers using the pre-filled template
            below. When you receive a file ,  or manually download one of the
            PUBLIC_-prefixed reports on county-taxes.net (see guide below) ,  upload it here.
            CSV and Excel (.xlsx) both accepted.
          </p>

          <select
            value={`${county.state}|${county.county}`}
            onChange={(e) => {
              const [st, c] = e.target.value.split("|");
              setCounty({ state: st || "", county: c || "" });
            }}
            data-testid="discovery-tax-county-select"
            style={{ colorScheme: "dark" }}
            className="w-full bg-slate-900 border border-white/10 rounded-sm px-2 py-1.5 text-xs text-slate-200"
          >
            <option value="|">Select a county...</option>
            {counties.map((c) => (
              <option key={`${c.state}|${c.county}`} value={`${c.state}|${c.county}`}>
                {`${c.county} County, ${c.state}`}
              </option>
            ))}
          </select>
          {loading && counties.length === 0 && (
            <div className="text-[11px] text-slate-500">Loading counties...</div>
          )}
          {loadErrors.counties && (
            <div data-testid="discovery-counties-error" className="text-[11px] text-red-300 flex items-center gap-2">
              {loadErrors.counties}
              <button onClick={load} data-testid="discovery-counties-retry" className="underline">Retry</button>
            </div>
          )}

          {PUBLIC_REPORT_GUIDE[county.county] && (
            <div
              data-testid="discovery-tax-report-guide"
              className="text-[11px] leading-relaxed bg-white/3 border border-white/10 rounded-sm p-3 space-y-1.5"
            >
              <div className="text-emerald-300 uppercase tracking-wider text-[10px]">
                Manual download procedure ,  {county.county} County
              </div>
              <div>
                Portal:{" "}
                <a href={PUBLIC_REPORT_GUIDE[county.county].portal} target="_blank" rel="noreferrer"
                   className="text-emerald-400 underline">
                  {PUBLIC_REPORT_GUIDE[county.county].portal}
                </a>
              </div>
              <ul className="list-disc list-inside text-slate-400 space-y-0.5">
                {PUBLIC_REPORT_GUIDE[county.county].reports.map((r) => <li key={r}>{r}</li>)}
              </ul>
              <div className="text-slate-500">
                Recommended refresh: {PUBLIC_REPORT_GUIDE[county.county].cadence}
              </div>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={genEmail}
              disabled={!county.county}
              data-testid="discovery-tax-gen-email"
              className="btn-ghost text-xs inline-flex items-center gap-1.5"
            >
              <Mail className="h-3 w-3" /> Generate request email
            </button>
            <label className="btn-ghost text-xs inline-flex items-center gap-1.5 cursor-pointer">
              <Upload className="h-3 w-3" /> {taxCsvBusy ? "Uploading..." : "Upload file"}
              <input
                type="file"
                accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => uploadTax(e.target.files?.[0])}
                disabled={taxCsvBusy || !county.county}
                data-testid="discovery-tax-file"
                className="hidden"
              />
            </label>
          </div>

          {lastTaxUpload && (
            <div
              data-testid="discovery-tax-upload-status"
              className={`text-[11px] rounded-sm p-3 border leading-relaxed ${
                lastTaxUpload.ok === true
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
                  : lastTaxUpload.ok === false
                  ? "bg-red-500/10 border-red-500/30 text-red-200"
                  : "bg-sky-500/10 border-sky-500/30 text-sky-200"
              }`}
            >
              <div className="flex items-center gap-1.5 font-medium">
                {lastTaxUpload.ok === true && <CheckCircle2 className="h-3.5 w-3.5" />}
                {lastTaxUpload.ok === false && <XCircle className="h-3.5 w-3.5" />}
                {lastTaxUpload.processing && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                {lastTaxUpload.filename} ,  {lastTaxUpload.county} County, {lastTaxUpload.state}
              </div>
              {lastTaxUpload.processing ? (
                <div className="mt-1 text-slate-300">
                  {lastTaxUpload.total_rows ? (
                    <>
                      Processing... {lastTaxUpload.processed_rows} / {lastTaxUpload.total_rows} rows
                      {" "}({lastTaxUpload.created} created, {lastTaxUpload.updated} updated)
                      ,  large files can take several minutes, this keeps updating.
                    </>
                  ) : (
                    "Reading file... (large exports can take a moment to parse before row progress appears)"
                  )}
                </div>
              ) : lastTaxUpload.ok ? (
                <div className="mt-1 text-slate-300">
                  {lastTaxUpload.found} rows found · {lastTaxUpload.created} created ·{" "}
                  {lastTaxUpload.updated} updated · {lastTaxUpload.skipped} skipped
                  {lastTaxUpload.errors > 0 && ` · ${lastTaxUpload.errors} errors`}
                  {lastTaxUpload.unmapped_columns?.length > 0 && (
                    <div className="mt-1 text-amber-300">
                      Unmapped columns (ignored): {lastTaxUpload.unmapped_columns.join(", ")}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-1">{lastTaxUpload.error || "Upload failed"}</div>
              )}
              <div className="mt-1 text-slate-500">{fmtDate(lastTaxUpload.at)}</div>
            </div>
          )}

          {emailTemplate && (
            <div className="space-y-2">
              <textarea
                value={emailTemplate}
                readOnly
                rows={12}
                data-testid="discovery-tax-email-preview"
                className="w-full bg-white/3 border border-white/10 rounded-sm p-2 text-[11px] font-mono text-slate-200 leading-relaxed"
              />
              <button onClick={copyEmail}
                      data-testid="discovery-tax-copy"
                      className="btn-ghost text-xs inline-flex items-center gap-1.5">
                <Download className="h-3 w-3" /> Copy email
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sunshine Law reply tracker */}
      <div className="ae-card p-5 space-y-3" data-testid="sunshine-law-tracker">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-emerald-400" />
          <h3 className="font-heading text-lg">Sunshine Law Request Tracker</h3>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          The 4 public-records requests sent to Broward, Miami-Dade, Palm Beach, and Martin
          (Block 134) go to info@acquigine.com's inbox ,  there's no automated inbox reader, so
          mark a request as replied here once someone reads the actual reply.
        </p>
        {loading && sunshineRequests.length === 0 && !loadErrors.sunshine && (
          <div className="text-[11px] text-slate-500">Loading requests...</div>
        )}
        {loadErrors.sunshine && (
          <div data-testid="sunshine-load-error" className="text-[11px] text-red-300 flex items-center gap-2">
            {loadErrors.sunshine}
            <button onClick={load} data-testid="sunshine-retry" className="underline">Retry</button>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          {sunshineRequests.map((req) => (
            <div key={req.county} data-testid={`sunshine-request-${req.county}`}
                 className="bg-white/3 border border-white/10 rounded-sm p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm text-slate-200">{req.county} County</div>
                <span
                  data-testid={`sunshine-status-${req.county}`}
                  className={`text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 font-mono ${
                    req.status === "replied"
                      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                      : "bg-amber-500/15 text-amber-300 border-amber-500/40"
                  }`}
                >
                  {req.status === "replied" ? "Replied" : "Pending"}
                </span>
              </div>
              <div className="text-[10px] text-slate-500">
                Sent to {req.to_email} on {fmtDate(req.sent_at)}
              </div>
              {req.status === "replied" ? (
                <div className="text-[11px] text-slate-300 space-y-1">
                  <div className="whitespace-pre-wrap">{req.reply_note}</div>
                  {req.has_attachment && (
                    <div className="text-emerald-300">
                      Attachment: {req.attachment_description || "yes (no description)"}
                    </div>
                  )}
                  <div className="text-slate-500">by {req.replied_by} · {fmtDate(req.replied_at)}</div>
                  <button
                    onClick={() => resetReply(req.county)}
                    disabled={sunshineBusy[req.county]}
                    data-testid={`sunshine-reset-${req.county}`}
                    className="btn-ghost text-[10px] mt-1"
                  >
                    Reset to pending
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <textarea
                    placeholder="Paste what the county said / attached..."
                    rows={3}
                    value={replyDraft[req.county]?.note || ""}
                    onChange={(e) => setReplyDraft((m) => ({
                      ...m, [req.county]: { ...m[req.county], note: e.target.value },
                    }))}
                    data-testid={`sunshine-reply-note-${req.county}`}
                    className="w-full bg-slate-900 border border-white/10 rounded-sm p-1.5 text-[11px] text-slate-200"
                    style={{ colorScheme: "dark" }}
                  />
                  <label className="flex items-center gap-1.5 text-[10px] text-slate-400">
                    <input
                      type="checkbox"
                      checked={!!replyDraft[req.county]?.hasAttachment}
                      onChange={(e) => setReplyDraft((m) => ({
                        ...m, [req.county]: { ...m[req.county], hasAttachment: e.target.checked },
                      }))}
                      data-testid={`sunshine-has-attachment-${req.county}`}
                    />
                    County attached a file
                  </label>
                  {replyDraft[req.county]?.hasAttachment && (
                    <input
                      type="text"
                      placeholder="e.g. delinquent_2026.xlsx, 4,200 rows"
                      value={replyDraft[req.county]?.attachmentDesc || ""}
                      onChange={(e) => setReplyDraft((m) => ({
                        ...m, [req.county]: { ...m[req.county], attachmentDesc: e.target.value },
                      }))}
                      data-testid={`sunshine-attachment-desc-${req.county}`}
                      className="w-full bg-slate-900 border border-white/10 rounded-sm p-1.5 text-[11px] text-slate-200"
                    />
                  )}
                  <button
                    onClick={() => markReplied(req.county)}
                    disabled={sunshineBusy[req.county]}
                    data-testid={`sunshine-mark-replied-${req.county}`}
                    className="btn-ghost text-xs inline-flex items-center gap-1.5"
                  >
                    <CheckCircle2 className="h-3 w-3" /> Mark as replied
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="ae-card p-4 bg-amber-500/5 border-amber-500/30 text-xs text-amber-200 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <strong>Compliance reminder:</strong> All discovered leads come from public records or
          voluntary broker submissions, never scraped private data. Per CAN-SPAM and TCPA, automated
          outreach to these owners requires an opt-out mechanism and identifies the subscriber as
          the sender, not Acquigine.
        </div>
      </div>
    </div>
  );
}
